import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import useRequest from './hooks/use-req';
import { showToast } from './toast';
import { GreenThick } from '../assets/icon';
import Button from './button';

const INTERESTS = [
    "Food / Restaurants",
    "Gadgets / Tech",
    "Fashion / Style",
    "Finance / Money Tips",
    "Relationships",
    "Work / Career Tips",
    "Travel",
    "Beauty / Skincare",
    "Home Services",
    "Parenting / Family",
    "Spiritual Matters",
    "Events / Experiences",
    "Cars / Mechanics",
];

const MIN_SELECTIONS = 5;
const MAX_SELECTIONS = 5;

interface InterestSelectorProps {
    onSuccess?: () => void;
    onSkip?: () => void;
    onClose?: () => void;
    showSkip?: boolean;
    variant?: 'page' | 'modal';
    title?: string;
    description?: string;
    buttonText?: string;
    skipText?: string;
}

const InterestSelector: React.FC<InterestSelectorProps> = ({
    onSuccess,
    onSkip,
    onClose,
    showSkip = true,
    variant = 'page',
    title = "Tell us your interests 👋",
    description = "Tell us what you care about so we can personalise rewards, deals, and recommendations for you.",
    buttonText = "Save and Proceed",
    skipText = "Skip Step",
}) => {
    const authToken = localStorage.getItem("authToken");
    const { handleSubmit } = useForm();
    const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
    const { loading, makeRequest } = useRequest('/account/interests', 'PUT', {
        Authorization: `Bearer ${authToken}`,
    });

    const toggleInterest = (interest: string) => {
        setSelectedInterests((prev) => {
            if (prev.includes(interest)) {
                return prev.filter((i) => i !== interest);
            }
            if (prev.length >= MAX_SELECTIONS) {
                showToast(`You can select up to ${MAX_SELECTIONS} interests`, false);
                return prev;
            }
            return [...prev, interest];
        });
    };

    const handleUserInterest = handleSubmit(async () => {
        if (selectedInterests.length < MIN_SELECTIONS) {
            showToast(`Please select at least ${MIN_SELECTIONS} interests`, false);
            return;
        }

        const interestsString = selectedInterests.join(', ');

        const [response] = await makeRequest(
            { interests: interestsString }
        );

        if (response?.status) {
            showToast('Interests saved successfully!', true);
            if (onSuccess) {
                onSuccess();
            }
        } else {
            showToast(response?.message || 'Failed to save interests', false);
        }
    });

    const handleSkip = () => {
        if (onSkip) {
            onSkip();
        }
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    const content = (
        <div className={variant === 'modal' ? 'p-6' : ''}>
            <div className="mb-6">
                {variant === 'modal' && onClose && (
                    <button
                        onClick={handleClose}
                        className="float-right text-gray-500 hover:text-gray-700"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                )}
                <h2 className="text-xl font-bold text-gray-900 mb-1">
                    {title}
                </h2>
                <p className="text-gray-500 text-sm mb-1">
                    {description}
                </p>
                <p className="text-gray-900 font-semibold text-sm mb-5">
                    You can select up to {MAX_SELECTIONS} interests.
                </p>

                {/* Selected count indicator */}
                <div className="mb-4 text-sm">
                    <span className={`font-medium ${selectedInterests.length >= MIN_SELECTIONS ? 'text-green-600' : 'text-gray-600'}`}>
                        {selectedInterests.length} / {MAX_SELECTIONS} selected
                    </span>
                    {selectedInterests.length >= MIN_SELECTIONS && (
                        <span className="ml-2 text-green-600">✓ Minimum met</span>
                    )}
                </div>
            </div>

            {/* Interest Chips */}
            <div className="flex flex-wrap gap-3 mb-6">
                {INTERESTS.map((interest) => {
                    const isSelected = selectedInterests.includes(interest);
                    const isDisabled = !isSelected && selectedInterests.length >= MAX_SELECTIONS;
                    return (
                        <button
                            key={interest}
                            type="button"
                            onClick={() => toggleInterest(interest)}
                            disabled={isDisabled}
                            className={`
                                flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold
                                transition-all duration-150
                                ${isSelected
                                    ? 'bg-gray-100 border-gray-300 text-gray-900 cursor-pointer'
                                    : isDisabled
                                        ? 'bg-white border-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50 cursor-pointer'
                                }
                            `}
                        >
                            {isSelected && <GreenThick />}
                            {interest}
                        </button>
                    );
                })}
            </div>

            {/* Action Buttons */}
            <Button
                onClick={handleUserInterest}
                variant="primary"
                disabled={loading || selectedInterests.length < MIN_SELECTIONS}
                className="w-full"
            >
                {loading ? 'Saving...' : buttonText}
            </Button>

            {/* Skip Link */}
            {showSkip && (
                <button
                    onClick={handleSkip}
                    className="text-center my-4 flex gap-1 justify-center font-medium text-sm mt-4 text-[#121212] w-full"
                >
                    Do this later?
                    <span className="underline">{skipText}</span>
                </button>
            )}
        </div>
    );

    return (
        <>
            {variant === 'modal' ? (
                // Modal wrapper
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
                        {content}
                    </div>
                </div>
            ) : (
                // Page wrapper
                <div className="flex justify-center items-center">
                    <div className="w-full max-w-2xl">
                        {content}
                    </div>
                </div>
            )}
        </>
    );
};

export default InterestSelector;